import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type MenuKey = "mobile_bottom" | "mobile_drawer" | "desktop_navbar";
export type RoleKey = "caregiver" | "client" | "b2b" | "guest";

export interface NavMenuItem {
  id: string;
  menu_key: MenuKey;
  role_key: RoleKey;
  label: string;
  path: string;
  icon: string;
  section: string | null;
  sort_order: number;
  is_active: boolean;
}

/**
 * Fetch navigation menu items for a given menu + role.
 * Returns active items sorted by sort_order. If the query fails or returns
 * nothing, the consumer should fall back to a hardcoded default to avoid
 * breaking navigation.
 */
export function useNavMenu(menu: MenuKey, role: RoleKey) {
  return useQuery({
    queryKey: ["nav-menu", menu, role],
    queryFn: async (): Promise<NavMenuItem[]> => {
      const { data, error } = await supabase
        .from("nav_menu_items")
        .select("*")
        .eq("menu_key", menu)
        .eq("role_key", role)
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return (data ?? []) as NavMenuItem[];
    },
    staleTime: 1000 * 60 * 5,
  });
}

/** Admin variant — returns ALL items (also inactive) for management UI. */
export function useNavMenuAdmin(menu: MenuKey, role: RoleKey) {
  return useQuery({
    queryKey: ["nav-menu-admin", menu, role],
    queryFn: async (): Promise<NavMenuItem[]> => {
      const { data, error } = await supabase
        .from("nav_menu_items")
        .select("*")
        .eq("menu_key", menu)
        .eq("role_key", role)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return (data ?? []) as NavMenuItem[];
    },
    staleTime: 0,
  });
}
